import {Container} from '@mui/material';
import {useEffect} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router';
import Box from '../../components/Box';
import Footer from '../../containers/Footer';
import HelpLanguageSwitcher from '../../help-center/components/HelpLanguageSwitcher';
import {HELP_SEARCH_INPUT_ID} from '../../help-center/constants';
import {useHelpLocaleAutoRedirect} from '../../help-center/hooks/useHelpLocale';
import {isHelpArticlePath} from '../../help-center/locales';
import {HelpPlatformProvider} from '../../help-center/PlatformContext';
import {useAppBarHeight} from '../../hooks';

const Component = () => {
  const appBarHeight = useAppBarHeight();
  const {pathname} = useLocation();
  // Article pages get a wider container to fit the sticky table of contents
  const isArticleView = isHelpArticlePath(pathname);
  // First help visit with a fr/de/it/es UI language on a bare (English) URL:
  // send the visitor to their locale's URLs (once per session, so an explicit
  // English pick in the switcher sticks).
  const redirectTarget = useHelpLocaleAutoRedirect();

  // Cmd/Ctrl+K focuses the search input of the current help page
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        const input = document.getElementById(HELP_SEARCH_INPUT_ID);
        if (input) {
          event.preventDefault();
          input.scrollIntoView({block: 'center', behavior: 'smooth'});
          input.focus({preventScroll: true});
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return (
    <HelpPlatformProvider>
      <Box sx={{mt: `${appBarHeight}px`}}>
        <Container maxWidth={isArticleView ? 'lg' : 'md'} sx={{py: 4}}>
          <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
            <HelpLanguageSwitcher />
          </Box>
          <Outlet />
        </Container>
      </Box>
      <Footer />
    </HelpPlatformProvider>
  );
};

Component.displayName = 'HelpCenter';

export {Component};
